import { Col, Flex, Row } from 'antd'
import { TFunction } from 'i18next'

import { PhysicalExamination } from 'src/api/requests'
import { Text } from 'src/components'
import { useLocales } from 'src/locales'

type Props = {
  data: PhysicalExamination
}

const verifyObjectValues = (obj?: Record<string, unknown> | null) => {
  return Object.values(obj ?? {}).some(
    (item) => !!item && typeof item !== 'object'
  )
}

const getFormattedText = (
  obj: Record<string, unknown> | null | undefined,
  translationKey: string,
  t: TFunction
) => {
  return Object.entries(obj ?? {})
    .filter(([_, value]) => !!value && typeof value === 'boolean')
    .map(([key]) => t(`${translationKey}.${key}`))
    .join(', ')
}

export const ConsultationFormPhysicalExaminationDetails = ({ data }: Props) => {
  const { t } = useLocales()

  const hasConstitutionalType = verifyObjectValues(data.constitutionalType)
  const hasCutaneousTeguments = verifyObjectValues(data.cutaneousTeguments)
  const hasOropharynx = verifyObjectValues(data.oropharynx)
  const hasAbdominalSensitivity = verifyObjectValues(
    data.abdominalGastrointestinalAssessment?.abdominalSensitivity
  )
  const hasDigestiveSigns = verifyObjectValues(
    data.abdominalGastrointestinalAssessment?.digestiveSigns
  )
  const hasGenital = verifyObjectValues(data.genitalAnalRegionAssessment)
  const hasVitalIndices = verifyObjectValues(data.vitalPhysiologicalIndices)
  const chestAndLungs = data.chestAndLungs
  const hasChestAndLungs =
    !!chestAndLungs?.chestForm ||
    !!chestAndLungs?.auscultatory?.rales ||
    !!chestAndLungs?.auscultatory?.vesicularSounds
  const cardiacExamValue = data.cardiacExam?.auscultatoryCardiacExam?.value
  const abdominal = data.abdominalGastrointestinalAssessment

  const constitutionalType = getFormattedText(
    data.constitutionalType,
    'consultation_form.constitutional_type',
    t
  )
  const cutaneousTeguments = getFormattedText(
    data.cutaneousTeguments,
    'consultation_form.cutaneous_teguments',
    t
  )
  const formattedCutaneousTeguments = [
    cutaneousTeguments,
    data.cutaneousTeguments?.otherModifications?.value
  ]
    .filter(Boolean)
    .join(', ')

  const oropharynx = getFormattedText(
    data.oropharynx,
    'consultation_form.oropharynx',
    t
  )
  const liver = getFormattedText(
    data.abdominalGastrointestinalAssessment?.liver,
    'consultation_form.abdominal_gastrointestinal_assessment',
    t
  )
  const spleen = getFormattedText(
    data.abdominalGastrointestinalAssessment?.spleen,
    'consultation_form.abdominal_gastrointestinal_assessment',
    t
  )
  const abdominalSensitivity = getFormattedText(
    data.abdominalGastrointestinalAssessment?.abdominalSensitivity,
    'consultation_form.abdominal_gastrointestinal_assessment',
    t
  )
  const digestiveSigns = getFormattedText(
    data.abdominalGastrointestinalAssessment?.digestiveSigns,
    'consultation_form.abdominal_gastrointestinal_assessment',
    t
  )
  const genital = getFormattedText(
    data.genitalAnalRegionAssessment,
    'consultation_form.genital',
    t
  )
  const formattedGenital = [
    genital,
    data.genitalAnalRegionAssessment?.hasOtherPathologies?.value
  ]
    .filter(Boolean)
    .join(', ')

  const urination = getFormattedText(
    data.urinarySystemAssessment?.urination,
    'consultation_form.urinary',
    t
  )

  const defecation = getFormattedText(
    data.urinarySystemAssessment?.defecationCharacteristics,
    'consultation_form.urinary',
    t
  )

  const fecal = getFormattedText(
    data.urinarySystemAssessment?.fecalMassCharacteristics,
    'consultation_form.urinary',
    t
  )

  const isPositiveBilateralText = data.urinarySystemAssessment?.tappingSymptom
    ?.isPositiveBilateral
    ? t('consultation_form.urinary.isPositiveBilateral')
    : t('consultation_form.urinary.isNegativeBilateral')

  const tappingSymptom = data.urinarySystemAssessment?.tappingSymptom
    ?.isPositiveUnilateral?.isSelected
    ? `${t('consultation_form.urinary.positiveUnilateral')}, ${data.urinarySystemAssessment.tappingSymptom.isPositiveUnilateral.value}`
    : isPositiveBilateralText

  return (
    <Row gutter={[24, 24]}>
      <Col span={24}>
        <Text size="xl">
          {t('consultation_form.physical_examination_section')}
        </Text>
      </Col>

      {data.accusations && (
        <Col span={24}>
          <Text>
            {t('consultation_form.accusations')} -{' '}
            <Text weight={400}>{data?.accusations}</Text>
          </Text>
        </Col>
      )}

      {data.generalState && (
        <Col span={24}>
          <Text>
            {t('consultation_form.general_state.title')} -{' '}
            <Text weight={400}>
              {t(`consultation_form.general_state.${data.generalState}`)}
            </Text>
          </Text>
        </Col>
      )}

      {data.conscience && (
        <Col span={24}>
          <Text>
            {t('consultation_form.conscience.title')} -{' '}
            <Text weight={400}>
              {t(`consultation_form.conscience.${data.conscience}`)}
            </Text>
          </Text>
        </Col>
      )}

      {data.position && (
        <Col span={24}>
          <Text>
            {t('consultation_form.position.title')} -{' '}
            <Text weight={400}>
              {t(`consultation_form.position.${data.position}`)}
            </Text>
          </Text>
        </Col>
      )}

      {hasConstitutionalType && (
        <Col span={24}>
          <Text>
            {t('consultation_form.constitutional_type.title')} -{' '}
            <Text weight={400}>{constitutionalType}</Text>
          </Text>
        </Col>
      )}

      {hasVitalIndices && (
        <Col span={24}>
          <Text>{t('consultation_form.vital_physiological_indices')}: </Text>

          <Row gutter={[12, 12]} className="mt-16 ml-16">
            {data.vitalPhysiologicalIndices?.pulse != null && (
              <Col span={12}>
                <Text>
                  {t('consultation_form.pulse')} -{' '}
                  <Text weight={400}>
                    {data.vitalPhysiologicalIndices?.pulse}
                  </Text>
                </Text>
              </Col>
            )}

            {data.vitalPhysiologicalIndices?.temperature != null && (
              <Col span={12}>
                <Text>
                  {t('consultation_form.temperature')} -{' '}
                  <Text weight={400}>
                    {data.vitalPhysiologicalIndices?.temperature}
                  </Text>
                </Text>
              </Col>
            )}

            {data.vitalPhysiologicalIndices?.hypertension != null && (
              <Col span={12}>
                <Text>
                  {t('consultation_form.hypertension')} -{' '}
                  <Text weight={400}>
                    {data.vitalPhysiologicalIndices?.hypertension}
                  </Text>
                </Text>
              </Col>
            )}

            {data.vitalPhysiologicalIndices?.breathingFrequency != null && (
              <Col span={12}>
                <Text>
                  {t('consultation_form.breathing_frequency')} -{' '}
                  <Text weight={400}>
                    {data.vitalPhysiologicalIndices?.breathingFrequency}
                  </Text>
                </Text>
              </Col>
            )}
          </Row>
        </Col>
      )}

      <Col span={24}>
        <Text>
          {t('consultation_form.peripheral_lymph_nodes.title')} -{' '}
          <Text weight={400}>
            {t(
              `consultation_form.peripheral_lymph_nodes.details.${data.peripheralLymphNodes?.value}`
            )}
            {data.peripheralLymphNodes?.comments &&
              `: ${data.peripheralLymphNodes?.comments}`}
          </Text>
        </Text>
      </Col>

      {(hasCutaneousTeguments ||
        data.cutaneousTeguments?.otherModifications?.isSelected) && (
        <Col span={24}>
          <Text>
            {t('consultation_form.cutaneous_teguments.title')} -{' '}
            <Text weight={400}>{formattedCutaneousTeguments}</Text>
          </Text>
        </Col>
      )}

      {(hasOropharynx || data.oropharynx?.modification) && (
        <Col span={24}>
          <Text>
            {t('consultation_form.oropharynx.title')} -{' '}
            <Text weight={400}>{oropharynx}</Text>
          </Text>
        </Col>
      )}

      {hasChestAndLungs && (
        <Col span={24}>
          <Text>{t('consultation_form.chest_and_lungs.title')}: </Text>
          <Row gutter={[16, 16]} className="mt-16 ml-16">
            {chestAndLungs?.chestForm && (
              <Col span={24}>
                <Text>
                  {t('consultation_form.chest_and_lungs.chest_form')} -{' '}
                  <Text weight={400}>
                    {t(
                      `consultation_form.chest_and_lungs.${chestAndLungs.chestForm}`
                    )}
                  </Text>
                </Text>
              </Col>
            )}

            {(chestAndLungs?.auscultatory?.rales ||
              chestAndLungs?.auscultatory?.vesicularSounds) && (
              <Col span={24}>
                <Flex gap={24}>
                  <Text>
                    {t('consultation_form.chest_and_lungs.auscultatory')}:{' '}
                  </Text>

                  {chestAndLungs.auscultatory.rales && (
                    <Text>
                      {t('consultation_form.chest_and_lungs.rales')} -{' '}
                      <Text weight={400}>
                        {t(
                          `consultation_form.chest_and_lungs.${chestAndLungs.auscultatory.rales}`
                        )}
                      </Text>
                    </Text>
                  )}

                  {chestAndLungs.auscultatory.vesicularSounds && (
                    <Text>
                      {t('consultation_form.chest_and_lungs.vesicular_sounds')}{' '}
                      -{' '}
                      <Text weight={400}>
                        {t(
                          `consultation_form.chest_and_lungs.${chestAndLungs.auscultatory.vesicularSounds}`
                        )}
                      </Text>
                    </Text>
                  )}
                </Flex>
              </Col>
            )}
          </Row>
        </Col>
      )}

      {cardiacExamValue && (
        <Col span={24}>
          <Text>{t('consultation_form.cardiac_exam.title')}</Text>

          <Row gutter={16} className="mt-16 ml-16">
            <Col span={24}>
              <Text>
                {t('consultation_form.cardiac_exam.auscultatory_cardiac_exam')}{' '}
                -{' '}
                <Text weight={400}>
                  {t(`consultation_form.cardiac_exam.${cardiacExamValue}`)}
                  {data?.cardiacExam?.auscultatoryCardiacExam?.comments &&
                    `: ${data.cardiacExam.auscultatoryCardiacExam.comments}`}
                </Text>
              </Text>
            </Col>
          </Row>
        </Col>
      )}

      <Col span={24}>
        <Text>
          {t('consultation_form.abdominal_gastrointestinal_assessment.title')}
        </Text>

        <Row gutter={[16, 16]} className="mt-16 ml-16">
          {(liver || abdominal?.liver?.consistency) && (
            <Col span={24}>
              <Flex gap={16}>
                {liver && (
                  <Text>
                    {t(
                      'consultation_form.abdominal_gastrointestinal_assessment.liver'
                    )}{' '}
                    - <Text weight={400}>{liver}</Text>
                  </Text>
                )}

                {abdominal?.liver?.consistency && (
                  <Text>
                    {t(
                      'consultation_form.abdominal_gastrointestinal_assessment.consistency'
                    )}{' '}
                    -{' '}
                    <Text weight={400}>
                      {t(
                        `consultation_form.abdominal_gastrointestinal_assessment.${abdominal.liver.consistency}`
                      )}
                    </Text>
                  </Text>
                )}
              </Flex>
            </Col>
          )}

          {(spleen || abdominal?.spleen?.consistency) && (
            <Col span={24}>
              <Flex gap={16}>
                {spleen && (
                  <Text>
                    {t(
                      'consultation_form.abdominal_gastrointestinal_assessment.spleen'
                    )}{' '}
                    - <Text weight={400}>{spleen}</Text>
                  </Text>
                )}

                {abdominal?.spleen?.consistency && (
                  <Text>
                    {t(
                      'consultation_form.abdominal_gastrointestinal_assessment.consistency'
                    )}{' '}
                    -{' '}
                    <Text weight={400}>
                      {t(
                        `consultation_form.abdominal_gastrointestinal_assessment.${abdominal.spleen.consistency}`
                      )}
                    </Text>
                  </Text>
                )}
              </Flex>
            </Col>
          )}

          {abdominal?.intestinalMotility && (
            <Col span={24}>
              <Text>
                {t(
                  'consultation_form.abdominal_gastrointestinal_assessment.intestinalMotility'
                )}{' '}
                -{' '}
                <Text weight={400}>
                  {t(
                    `consultation_form.abdominal_gastrointestinal_assessment.${abdominal.intestinalMotility}`
                  )}
                </Text>
              </Text>
            </Col>
          )}

          {hasAbdominalSensitivity && (
            <Col span={24}>
              <Text>
                {t(
                  'consultation_form.abdominal_gastrointestinal_assessment.abdominalSensitivity'
                )}{' '}
                - <Text weight={400}>{abdominalSensitivity}</Text>
              </Text>
            </Col>
          )}

          {hasDigestiveSigns && (
            <Col span={24}>
              <Text>
                {t(
                  'consultation_form.abdominal_gastrointestinal_assessment.digestiveSigns'
                )}{' '}
                - <Text weight={400}>{digestiveSigns}</Text>
              </Text>
            </Col>
          )}
        </Row>
      </Col>

      {(hasGenital ||
        data.genitalAnalRegionAssessment?.hasOtherPathologies?.isSelected) && (
        <Col span={24}>
          <Text>
            {t('consultation_form.genital.title')} -{' '}
            <Text weight={400}>{formattedGenital}</Text>
          </Text>
        </Col>
      )}

      <Col span={24}>
        <Text>
          {t('consultation_form.neuorological.title')} -{' '}
          <Text weight={400}>
            {data.neurologicalStatusAssessment?.meningealSigns?.isPositive
              ?.isSelected
              ? t('tests_results.positive')
              : t('tests_results.negative')}
            {data.neurologicalStatusAssessment?.meningealSigns?.isPositive
              ?.value &&
              `, ${data.neurologicalStatusAssessment.meningealSigns.isPositive.value}`}
          </Text>
        </Text>
      </Col>

      <Col span={24}>
        <Text>
          {t('consultation_form.visual_functions.title')} -{' '}
          <Text weight={400}>
            {data.visualFunctionsAssessment?.isAffected?.isSelected
              ? t('consultation_form.affected')
              : t('consultation_form.isNotAffected')}
            {data.visualFunctionsAssessment?.isAffected?.value &&
              `, ${data.visualFunctionsAssessment.isAffected.value}`}
          </Text>
        </Text>
      </Col>

      <Col span={24}>
        <Text>
          {t('consultation_form.auditive_functions.title')} -{' '}
          <Text weight={400}>
            {data.auditoryFunctionsAssessment?.isAffected?.isSelected
              ? t('consultation_form.affected')
              : t('consultation_form.isNotAffected')}
            {data.auditoryFunctionsAssessment?.isAffected?.value &&
              `, ${data.auditoryFunctionsAssessment.isAffected.value}`}
          </Text>
        </Text>
      </Col>

      <Col span={24}>
        <Text>{t('consultation_form.urinary.title')}</Text>

        <Row gutter={[16, 16]} className="mt-16 ml-16">
          {data.urinarySystemAssessment?.urination && (
            <Col span={24}>
              <Text>
                {t('consultation_form.urinary.urination')} -{' '}
                <Text weight={400}>{urination}</Text>
              </Text>
            </Col>
          )}

          {data.urinarySystemAssessment?.tappingSymptom && (
            <Col span={24}>
              <Text>
                {t('consultation_form.urinary.tapping_symptom')} -{' '}
                <Text weight={400}>{tappingSymptom}</Text>
              </Text>
            </Col>
          )}

          {data.urinarySystemAssessment?.defecationCharacteristics && (
            <Col span={24}>
              <Text>
                {t('consultation_form.urinary.defecation')} -{' '}
                <Text weight={400}>{defecation}</Text>
              </Text>
            </Col>
          )}

          {data?.urinarySystemAssessment?.fecalMassCharacteristics && (
            <Col span={24}>
              <Text>
                {t('consultation_form.urinary.fecal')} -{' '}
                <Text weight={400}>{fecal}</Text>
              </Text>
            </Col>
          )}
        </Row>
      </Col>
    </Row>
  )
}
